<template>
	<form
		class="fade-in flex flex-col"
		autocomplete="off"
		@submit.prevent="
			() => {
				if (showCheckbox && !state.isChecked) return
				emit('submit')
			}
		"
	>
		<div v-if="showBack || title" class="flex items-center">
			<Icon
				v-if="showBack"
				name="mdi:arrow-left"
				class="text-neutral-900 me-2 h-8 w-8 transform cursor-pointer rounded-md duration-300 hover:text-primary rtl:rotate-180"
				@click="emit('back')"
			/>
			<span v-if="title" class="text-3xl font-bold">
				<span class="text-neutral-900">{{ title }}</span>
				<span class="text-primary" :style="{ color: color }">.</span>
			</span>
		</div>
		<span v-if="subtitle || subtitleLink" class="mt-2 text-sm">
			<span v-if="subtitle" class="text-neutral-700 me-2">
				{{ subtitle }}
			</span>
			<span
				v-if="subtitleLink"
				class="cursor-pointer font-medium text-primary"
				@click="emit('click-subtitle-link')"
			>
				{{ subtitleLink }}
			</span>
		</span>
		<span v-if="title || showBack || subtitle || subtitleLink" class="h-8" />
		<div
			v-if="showEditEmail"
			class="text-neutral-700 mb-8 flex cursor-pointer items-center self-center rounded-full bg-primary bg-opacity-25 pe-4 text-xs"
			@click="emit('edit-email')"
		>
			<Icon name="mdi:email" class="mx-3 my-2 h-5 w-5 rounded-full duration-300" />
			<span class="py-2" dir="ltr">{{ data.email }}</span>
		</div>
		<div
			v-if="showEditMobile"
			class="text-neutral-700 mb-8 flex cursor-pointer items-center self-center rounded-full bg-primary bg-opacity-25 pe-4 text-xs"
			@click="emit('edit-mobile')"
		>
			<Icon name="mdi:phone" class="mx-3 my-2 h-5 w-5 rounded-full duration-300" />
			<span class="py-2" dir="ltr">{{ data.mobile }}</span>
		</div>
		<div
			v-if="showEditStaffNumber"
			class="text-neutral-700 mb-8 flex cursor-pointer items-center self-center rounded-full bg-primary bg-opacity-25 pe-4 text-xs"
			@click="emit('edit-staff-number')"
		>
			<Icon name="mdi:account" class="mx-3 my-2 h-5 w-5 rounded-full duration-300" />
			<span class="py-2" dir="ltr">{{ data.staff_number }}</span>
		</div>
		<div v-if="showModeSelector" class="mb-8 flex w-full max-w-lg self-center overflow-hidden rounded-t-xl px-4">
			<div
				v-for="m in modes"
				:key="m.id"
				:class="[
					'flex-shrink-0 flex-grow cursor-pointer truncate border-b-4 px-4 py-3 text-center text-sm duration-300',
					mode == m.id
						? 'border-primary bg-primary bg-opacity-10 font-bold text-primary'
						: 'text-slate-800 bg-slate-50 border-slate-200 font-normal hover:bg-slate-100'
				]"
				@click="() => emit('update:mode', m.id)"
			>
				{{ m.title }}
			</div>
		</div>
		<div class="grid grid-cols-1 gap-6">
			<slot name="content-top"></slot>
			<BaseTextInput
				v-if="showStaffNumber"
				:placeholder="$t('staff_number')"
				:value="data.staff_number"
				name="staff_number"
				end-icon="mdi:user"
				:size="size"
				:disabled="disabled"
				theme-type="outlined"
				list="saved_staff_number_list"
				@change="(v) => changeData({ staff_number: v })"
			/>
			<ClientOnly>
				<datalist v-if="savedStaffNumberList" id="saved_staff_number_list">
					<option v-for="number in savedStaffNumberList" :key="number" :value="number">
						{{ number }}
					</option>
				</datalist>
			</ClientOnly>
			<BaseTextInput
				v-if="showEmail"
				:placeholder="$t('email')"
				:value="data.email"
				name="email"
				end-icon="mdi:email"
				:size="size"
				:disabled="disabled"
				:autocomplete="showRePassword ? '' : 'email'"
				theme-type="outlined"
				@change="(v) => changeData({ email: v })"
			/>
			<BaseMobileInput
				v-if="showMobile"
				:size="size"
				:model-value="data.mobile"
				@update:model-value="(v) => changeData({ mobile: v })"
			/>
			<BaseTextInput
				v-if="showFirstname"
				:placeholder="$t('firstname')"
				:value="data.firstname"
				name="firstname"
				end-icon="mdi:account"
				:disabled="disabled"
				theme-type="outlined"
				:size="size"
				@change="(v) => changeData({ firstname: v })"
			/>
			<BaseTextInput
				v-if="showLastname"
				:placeholder="$t('lastname')"
				name="lastname"
				:value="data.lastname"
				end-icon="mdi:account"
				:disabled="disabled"
				theme-type="outlined"
				:size="size"
				@change="(v) => changeData({ lastname: v })"
			/>
			<BaseTextInput
				v-if="showPassword"
				type="password"
				:placeholder="$t('password')"
				:value="data.password"
				end-icon="mdi:lock"
				:autocomplete="showRePassword ? 'new-password' : 'current-password'"
				:disabled="disabled"
				theme-type="outlined"
				:size="size"
				@change="(v) => changeData({ password: v })"
			/>
			<input v-if="showEmail || showMobile" type="password" hidden :value="data.password || undefined" />
			<BaseTextInput
				v-if="showRePassword"
				type="password"
				:placeholder="$t('repeat_password')"
				:value="data.re_password"
				end-icon="mdi:lock"
				autocomplete="new-password"
				:disabled="disabled"
				theme-type="outlined"
				:size="size"
				@change="(v) => changeData({ re_password: v })"
			/>
			<BaseTextInput
				v-if="showCode"
				type="text"
				:placeholder="$t('confirmation_code')"
				:value="data.code"
				end-icon="material-symbols:verified-user"
				:disabled="disabled"
				theme-type="outlined"
				:size="size"
				@change="(v) => changeData({ code: v })"
			/>
			<BaseCodeInput
				v-if="showCode2"
				:length="6"
				:model-value="data.code"
				@update:model-value="(v) => changeData({ code: v })"
				@done="emit('submit')"
			/>
		</div>
		<div class="flex-grow"></div>
		<span
			v-if="showCheckbox"
			class="-mb-2 mt-8 flex items-center self-start"
			@click="state.isChecked = !state.isChecked"
		>
			<BaseCheckBox :model-value="state.isChecked" class="me-2.5 h-5 w-5 flex-shrink-0" />
			<span class="text-slate-700 text-xs">
				<I18nT scope="global" keypath="auth_checkbox_text">
					<template #app_name>
						{{ $t("app_name") }}
					</template>
					<template #terms>
						<NuxtLink
							:to="{
								path: $routeUrl.RulesUrl(),
								query: {
									page: 0
								}
							}"
							class="font-semibold text-primary"
						>
							{{ $t("terms_of_use") }}
						</NuxtLink>
					</template>
					<template #privacy>
						<NuxtLink
							:to="{
								path: $routeUrl.RulesUrl(),
								query: {
									page: 3
								}
							}"
							class="font-semibold text-primary"
						>
							{{ $t("privacy") }}
						</NuxtLink>
					</template>
				</I18nT>
			</span>
		</span>
		<button
			v-if="submitTitle"
			type="submit"
			:class="[
				'text-white mb-2 mt-8 cursor-pointer rounded-lg px-6 py-4 text-center font-bold',
				showCheckbox && !state.isChecked
					? 'ripple-bg-slate-400 pointer-events-none'
					: color
						? ''
						: 'ripple-bg-primary'
			]"
			:style="{
				backgroundColor: color
			}"
		>
			{{ submitTitle }}
		</button>
		<span
			v-if="bottomLink"
			class="text-neutral-700 mt-2 cursor-pointer self-center text-sm font-bold duration-300 hover:text-primary"
			@click="emit('click-bottom-link')"
		>
			{{ bottomLink }}
		</span>
	</form>
</template>

<script setup lang="ts">
import { type UserAuthMode } from "~/stores/user"

const userStore = useUserStore()

type AuthFormData = {
	staff_number?: string
	email?: string
	mobile?: string
	firstname?: string
	lastname?: string
	captcha?: string
	captcha_key?: string
	password?: string
	re_password?: string
	code?: string
}

const emit = defineEmits<{
	change: [AuthFormData]
	submit: []
	back: []
	"update:loading": [boolean]
	"update:mode": [UserAuthMode["id"]]
	"click-subtitle-link": []
	"edit-email": []
	"edit-mobile": []
	"edit-staff-number": []
	"click-bottom-link": []
}>()

const props = withDefaults(
	defineProps<{
		title?: string
		subtitle?: string
		subtitleLink?: string
		bottomLink?: string
		submitTitle?: string
		showEmail?: boolean
		showMobile?: boolean
		showCode?: boolean
		showCode2?: boolean
		showBack?: boolean
		showCaptcha?: boolean
		showPassword?: boolean
		showRePassword?: boolean
		showEditEmail?: boolean
		showEditMobile?: boolean
		showEditStaffNumber?: boolean
		showFirstname?: boolean
		showLastname?: boolean
		showCheckbox?: boolean
		showModeSelector?: boolean
		showStaffNumber?: boolean
		savedStaffNumberList?: string[]
		color?: string
		data: AuthFormData
		wrongCount?: number
		disabled?: boolean
		mode?: UserAuthMode["id"]
		size?: "md" | "lg"
	}>(),
	{
		size: "md"
	}
)

const state = reactive({
	center: [-104.006, 40.7128],
	isSelectLocationDialogVisible: false,
	modalLoading: false,
	isChecked: false
})

const modes = computed(() => userStore.authModes)

const changeData = (d: AuthFormData | any) => {
	emit("change", {
		...props.data,
		...d
	})
}
</script>
